import { Close, Search as SearchIcon } from '@tunasong/icons'
import type { SearchSummary } from '@tunasong/models'
import type { Entity, Persisted, SearchFilter } from '@tunasong/schemas'
import { Button, LinearProgress, TagList, disableAutocomplete, useDelayedValue } from '@tunasong/ui-lib'
import cn from 'classnames'
import type { ChangeEvent, FocusEvent, KeyboardEvent, ReactElement, ReactNode } from 'react'
import { useId, useRef, useState } from 'react'
import styles from './search-bar.module.css'
import { SearchListbox } from './search-listbox.js'
import { optionId } from './search-listbox-ids.js'

/** The scope control (#2555): shown only when search was opened inside a space */
export interface SearchScope {
  /** The space's name, as in "In {space}" */
  space: string
  /** Searching only that space */
  scoped: boolean
  onChange(scoped: boolean): void
}

export interface SearchBarProps<T extends SearchSummary | Persisted<Entity>> {
  className?: string
  placeholder?: string
  loading?: boolean
  entities: T[]
  selected?: T
  query: string
  /** Select the query when the field takes focus: a pre-filled query that typing replaces (GH #2547) */
  selectQueryOnFocus?: boolean
  /** Every match across pages, for the live region; the loaded count when unknown */
  total?: number

  /** Filter in query. These are AND'ed  */
  filter?: SearchFilter

  /** Between the search controls and the results: Search's type chips (GH #2519 B) */
  aboveResults?: ReactNode

  scope?: SearchScope
  /** While scoped: the matches in other spaces, for the list's last line */
  otherSpacesCount?: number

  /** The list's name, and a visible heading over it: `Recent` with nothing typed (#2555 M5) */
  listLabel?: string
  heading?: string
  /** Shown in place of the list when there is nothing in it: no match, or a new account (#2555 M4) */
  emptyContent?: ReactNode
  /** The search failed: "Search isn't responding." with Try again, over the dimmed earlier results */
  error?: { onRetry(): void }

  /** `dialog`: Search's fixed results height (default); `popup`: the editor's @-mention, sized by its popup */
  variant?: 'dialog' | 'popup'

  /** On a phone, the header's Cancel (screen F) */
  onCancel?(): void

  renderHotkeys?(entity: T, active: boolean): ReactElement | null
  /** Query has changed */
  onQuery(query: string): void
  onChange?(entity: T): void
  /** Cmd or Ctrl+Enter, or a Cmd or Ctrl+click */
  onOpenInNewTab?(entity: T): void
  /** Alt+Enter on the active option: the editor's "Insert a link" (Joni, #2562) */
  onAltEnter?(entity: T): void
  onEndOfResults?(): Promise<void>
  /** After the combobox's own keys, e.g. the mention menu tracking Shift */
  onKeyDown?(ev: KeyboardEvent<HTMLInputElement>): void
  onKeyUp?(ev: KeyboardEvent<HTMLInputElement>): void
}

/** Joni §A11y: one polite count, once typing settles; never 60 announcements a minute */
const announceDelay = 500

/**
 * Search's input and results (#2555): the WAI-ARIA combobox pattern. Focus stays in the input, which names the active
 * option with `aria-activedescendant`; ↑/↓ move it, Enter opens it, Cmd/Ctrl+Enter opens it in a new tab, and Esc
 * clears the text before it closes the dialog. Tab moves on to the scope and the type chips.
 */
export function SearchBar<T extends SearchSummary>(props: SearchBarProps<T>) {
  const {
    className,
    query,
    selectQueryOnFocus = false,
    loading = false,
    placeholder = 'Search songs, setlists, bands and people',
    entities,
    selected,
    total,
    filter,
    aboveResults,
    scope,
    otherSpacesCount,
    listLabel = 'Results',
    heading,
    emptyContent,
    error,
    onCancel,
    variant = 'dialog',
    renderHotkeys,
    onChange,
    onOpenInNewTab,
    onAltEnter,
    onQuery,
    onEndOfResults,
    onKeyDown,
    onKeyUp,
  } = props
  const inputId = useId()
  const listboxId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  /**
   * The active option, by id (Joni, #2562): a refresh that reorders or pages the results keeps it on the same entity,
   * and one that drops it leaves none active.
   */
  const [activeId, setActiveId] = useState<string | undefined>(selected?.id)
  const activeIndex = activeId ? entities.findIndex(e => e.id === activeId) : -1
  const setActive = (index: number) => setActiveId(index >= 0 ? entities[index]?.id : undefined)

  const tags = [filter?.tags ?? [], filter?.types?.map(type => `type:${type}`) ?? []].flat()

  const count = total ?? entities.length
  const announcement = useDelayedValue(
    query && !loading ? `${count} ${count === 1 ? 'result' : 'results'}` : '',
    announceDelay
  )

  const setQuery = (next: string) => {
    setActive(-1)
    onQuery(next)
  }

  const open = (entity: T | undefined, newTab: boolean) => {
    if (!entity) {
      return
    }
    if (newTab) {
      onOpenInNewTab?.(entity)
      return
    }
    onChange?.(entity)
  }

  const handleKeyDown = (ev: KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === 'ArrowDown') {
      ev.preventDefault()
      setActive(Math.min(activeIndex + 1, entities.length - 1))
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault()
      setActive(Math.max(activeIndex - 1, -1))
    } else if (ev.key === 'Enter' && ev.altKey) {
      ev.preventDefault()
      const entity = entities[activeIndex]
      if (entity) {
        onAltEnter?.(entity)
      }
    } else if (ev.key === 'Enter') {
      ev.preventDefault()
      open(entities[activeIndex], ev.metaKey || ev.ctrlKey)
    } else if (ev.key === 'Escape' && query) {
      /** Esc clears first; with nothing typed it reaches the dialog, which closes */
      ev.preventDefault()
      ev.stopPropagation()
      setQuery('')
    }
    onKeyDown?.(ev)
  }

  /**
   * A radiogroup moves with the arrow keys, and focus moves with the check (Joni, #2562): with two radios, the arrows
   * always land on the other one. Tab enters the group on the checked radio.
   */
  const handleScopeKey = (ev: KeyboardEvent<HTMLButtonElement>) => {
    if (scope && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(ev.key)) {
      ev.preventDefault()
      ev.currentTarget.parentElement?.querySelector<HTMLElement>('[role="radio"][aria-checked="false"]')?.focus()
      scope.onChange(!scope.scoped)
    }
  }

  /** "Search everywhere" widens the search, and typing carries on where it was */
  const handleSearchEverywhere = () => {
    scope?.onChange(false)
    inputRef.current?.focus()
  }

  return (
    <div className={cn(styles.container, className)}>
      <div className={styles.header}>
        <label htmlFor={inputId} className={styles.visuallyHidden}>
          Search Tunasong
        </label>
        <div className={styles.field}>
          <SearchIcon className={styles.fieldIcon} aria-hidden="true" />
          <input
            {...disableAutocomplete}
            ref={inputRef}
            id={inputId}
            className={styles.input}
            role="combobox"
            aria-expanded={entities.length > 0 && !error}
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={activeIndex >= 0 && !error ? optionId(listboxId, activeIndex) : undefined}
            autoFocus
            value={query}
            placeholder={placeholder}
            onChange={(ev: ChangeEvent<HTMLInputElement>) => setQuery(ev.target.value)}
            onKeyDown={handleKeyDown}
            onKeyUp={onKeyUp}
            onFocus={selectQueryOnFocus ? (ev: FocusEvent<HTMLInputElement>) => ev.currentTarget.select() : undefined}
          />
          {tags.length > 0 ? <TagList tags={tags} /> : null}
          {query ? (
            <button type="button" className={styles.clear} aria-label="Clear search" onClick={() => setQuery('')}>
              <Close aria-hidden="true" />
            </button>
          ) : null}
        </div>
        {onCancel ? (
          <button type="button" className={styles.cancel} onClick={onCancel}>
            Cancel
          </button>
        ) : null}
      </div>

      <div className={styles.controls}>
        {scope ? (
          <div role="radiogroup" aria-label="Search scope" className={styles.scope}>
            {[true, false].map(scoped => (
              <button
                key={String(scoped)}
                type="button"
                role="radio"
                aria-checked={scope.scoped === scoped}
                tabIndex={scope.scoped === scoped ? 0 : -1}
                className={cn(styles.scopeOption, { [styles.scopeChecked]: scope.scoped === scoped })}
                onClick={() => scope.onChange(scoped)}
                onKeyDown={handleScopeKey}
              >
                {scoped ? `In ${scope.space}` : 'Everywhere'}
              </button>
            ))}
          </div>
        ) : null}
        {aboveResults}
      </div>

      {/* M4: a thin bar while it searches; the earlier results stay, with no "Nothing matches" flash */}
      <div className={styles.progress}>{loading ? <LinearProgress aria-label="Searching" /> : null}</div>

      {error ? (
        <div className={styles.error} role="alert">
          <span>Search isn’t responding.</span>
          <Button variant="outlined" color="error" size="small" onClick={error.onRetry}>
            Try again
          </Button>
        </div>
      ) : null}

      <SearchListbox
        id={listboxId}
        label={listLabel}
        heading={heading}
        highlight={query}
        dimmed={Boolean(error)}
        empty={!loading && !error && entities.length === 0 ? emptyContent : null}
        entities={entities}
        activeIndex={activeIndex}
        renderAction={renderHotkeys}
        onOpen={(entity, { newTab }) => open(entity, newTab)}
        onEndOfResults={onEndOfResults}
        fixedHeight={variant === 'dialog'}
        footer={
          /** The footer ends a list; on a scoped miss the empty state offers Search everywhere already (GH #2731) */
          scope?.scoped && otherSpacesCount && entities.length > 0 ? (
            <p className={styles.moreElsewhere}>
              <span>{otherSpacesCount} more in other spaces</span>
              <span aria-hidden="true">·</span>
              <button type="button" className={styles.link} onClick={handleSearchEverywhere}>
                Search everywhere
              </button>
            </p>
          ) : null
        }
      />

      <div role="status" aria-live="polite" className={styles.visuallyHidden}>
        {announcement}
      </div>
    </div>
  )
}
